import { Bed, Bath, Users, Crown, Sparkles, CheckCircle } from 'lucide-react'

interface DetailsTabProps {
  description: string
  summary?: string
  category?: { name: string }
  isFeatured: boolean
  isAvailable: boolean
  bed: number
  bath: number
  maxGuests: number
  rent: number
  listingStatusLabel?: string
  priceHint?: string
}

const DetailsTab = ({
  description,
  summary,
  category,
  isFeatured,
  isAvailable,
  bed,
  bath,
  maxGuests,
  rent,
  listingStatusLabel,
  priceHint,
}: DetailsTabProps) => {
  return (
    <div className="animate-fade-in-up">
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">About this stay</h3>

      {summary && (
        <p className="text-base text-gray-800 font-medium leading-relaxed mb-4 border-l-4 border-primary-400 pl-4">
          {summary}
        </p>
      )}

      {description ? (
        <div
          className="room-description-rich text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6 prose prose-sm max-w-none prose-p:my-3 prose-ul:my-2 prose-li:my-0.5"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      ) : (
        <p className="text-sm text-gray-500 mb-4">Description coming soon.</p>
      )}

      {(priceHint || listingStatusLabel) && (
        <div className="mb-4 rounded-xl bg-primary-50 border border-primary-100 px-4 py-3 text-sm text-primary-900">
          <span className="font-semibold">Market rate hint:</span>{' '}
          {priceHint || listingStatusLabel}
        </div>
      )}

      <div className="mb-4 sm:mb-6">
        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Highlights</h4>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {category && (
            <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm font-medium">
              <Sparkles className="h-3 w-3" />
              {category.name}
            </span>
          )}
          {isFeatured && (
            <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs sm:text-sm font-medium">
              <Crown className="h-3 w-3" />
              Featured
            </span>
          )}
          {isAvailable && (
            <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-0.5 sm:py-1 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium">
              <CheckCircle className="h-3 w-3" />
              Available
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg">
          <Bed className="h-4 w-4 sm:h-5 sm:w-5 text-primary-500 flex-shrink-0" />
          <div>
            <span className="text-xs sm:text-sm text-gray-700 font-medium">{bed} Beds</span>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg">
          <Bath className="h-4 w-4 sm:h-5 sm:w-5 text-primary-500 flex-shrink-0" />
          <div>
            <span className="text-xs sm:text-sm text-gray-700 font-medium">{bath} Bath</span>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg">
          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary-500 flex-shrink-0" />
          <div>
            <span className="text-xs sm:text-sm text-gray-700 font-medium">Up to {maxGuests} guests</span>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-gray-50 rounded-lg">
          <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-primary-500 flex-shrink-0" />
          <div>
            <span className="text-xs sm:text-sm text-gray-700 font-medium">₹{rent}/night</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DetailsTab
